import React, { forwardRef, isValidElement, memo, type ReactElement, type ReactNode } from 'react';

import classnames from 'classnames';

import { FieldLabel, type FieldLabelProps } from '../FieldLabel';
import { FormControlMessage } from '../FormControlMessage';

import { SelectRoot } from './primitives/root/SelectRoot';
import { useSelectRootContext } from './primitives/root/SelectRootContext';
import * as Primitives from './Select.parts';

import styles from './Select.module.scss';

import type { SelectTriggerColor } from './primitives/trigger/SelectTrigger';
import type {
    SelectRootProps,
    SelectRef,
    DropdownRenderFn,
    GetPopupContainerFn,
    MultipleSelectShowSearch
} from './Select.types';

export type SelectProps = SelectRootProps & {
    /**
     * An informatory tooltip that is displayed when hovering over the label icon.
     *
     * If a function is passed, it will be called with the icon element as argument:
     * ```
     * <FieldLabel label="Label" tooltip={(props) => <MyCustomTooltip title="Tooltip" {...props} />
     * ```
     */
    tooltip?: FieldLabelProps['tooltip'];
    /**
     * A text to appear at the top of the input, when provided a `label` element will wrap the input.
     */
    label?: string | null;
    /**
     * A text to appear at the top of the input, when provided a `label` element will wrap the input.
     */
    required?: boolean;
    /**
     * A custom class name for the wrapper element.
     */
    containerClassName?: string;
    state?: 'error' | 'alert';
    /**
     * Pass string to show a message below the field. The message will have a `error` style by default. Use `state` to change the style.
     */
    message?: string;
    /** Pass string to show a help message below the field. */
    helperText?: string;
    dropdownRender?: DropdownRenderFn;
    /**
     * Disable the default padding of the current selected option, so you can customize it.
     */
    disableValuePadding?: boolean;
    /**
     * Parent Node which the selector should be rendered to.
     * Default to body. When position issues happen,
     * try to modify it into scrollable content and position it relative.
     */
    getPopupContainer?: GetPopupContainerFn;
    /**
     * The className of dropdown menu
     */
    popupClassName?: string;
    /**
     * Specify content to show when no result matches the filter.
     */
    notFoundContent?: ReactNode;
    placeholder?: ReactNode;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onClear?: () => void;
    allowClear?: boolean;
    color?: SelectTriggerColor;
    showArrow?: boolean;
};

type MenuRendererProps = {
    searchText: string;
    menuNode: React.JSX.Element;
    dropdownRender?: DropdownRenderFn;
};

const MenuRenderer = memo(({ searchText, menuNode, dropdownRender }: MenuRendererProps) => {
    let popupNode = menuNode;
    if (typeof dropdownRender === 'function') {
        popupNode = dropdownRender(popupNode, { searchText });
    } else if (isValidElement(dropdownRender)) {
        popupNode = dropdownRender;
    }
    popupNode = React.Children.only(
        typeof popupNode === 'string' ? <span>{popupNode}</span> : popupNode
    );
    return popupNode;
});
MenuRenderer.displayName = 'MenuRenderer';

const SelectFieldCore = (props: SelectProps) => {
    const {
        label,
        tooltip,
        required,
        containerClassName,
        state,
        message,
        helperText,
        dropdownRender,
        disableValuePadding,
        getPopupContainer,
        popupClassName,
        notFoundContent,
        showSearch,
        placeholder,
        children,
        ...rest
    } = props;
    const { id, internalSearchValue, mode } = useSelectRootContext();
    const tooltipId = tooltip ? `${id}-tooltip` : '';
    const searchInputProps = (
        typeof showSearch === 'object' ? showSearch : {}
    ) as MultipleSelectShowSearch;
    const shouldShowDropdownSearch = Boolean(mode === 'multiple' && showSearch);
    return (
        <>
            <div className={classnames(styles.container, containerClassName)}>
                {label && (
                    <FieldLabel
                        htmlFor={id}
                        label={label}
                        tooltip={tooltip}
                        tooltipId={tooltipId}
                        required={required}
                    />
                )}
                <Primitives.Trigger customInput={children as ReactElement} {...rest}>
                    <Primitives.Value
                        className={classnames({
                            [styles.disableValuePadding]: disableValuePadding
                        })}
                    />
                    <Primitives.Placeholder>{placeholder}</Primitives.Placeholder>
                    <Primitives.Icon />
                </Primitives.Trigger>
                {helperText && <FormControlMessage message={helperText} type="helper" />}
                {message && <FormControlMessage message={message} type={state} />}
            </div>
            <Primitives.Dropdown getPopupContainer={getPopupContainer} className={popupClassName}>
                {shouldShowDropdownSearch && (
                    <Primitives.DropdownSearchInput {...searchInputProps} />
                )}
                <MenuRenderer
                    searchText={internalSearchValue}
                    menuNode={
                        <>
                            <Primitives.DropdownEmptyContent>
                                {notFoundContent}
                            </Primitives.DropdownEmptyContent>
                            <Primitives.Options />
                        </>
                    }
                    dropdownRender={dropdownRender}
                />
            </Primitives.Dropdown>
        </>
    );
};

const SelectFieldRoot = forwardRef<SelectRef, SelectProps>((props, ref) => {
    const { children, state, message, color, onBlur, onFocus, ...rest } = props;
    const mergedState = state ?? (message ? 'error' : undefined);
    const autoCompleteOpen = Boolean(props.options?.length > 0 || props.notFoundContent);

    return (
        <SelectRoot {...rest} autoCompleteOpen={autoCompleteOpen} ref={ref}>
            <SelectFieldCore
                onBlur={onBlur}
                onFocus={onFocus}
                message={message}
                state={state}
                color={color ?? mergedState}
                {...rest}
            >
                {children}
            </SelectFieldCore>
        </SelectRoot>
    );
});
SelectFieldRoot.displayName = 'Select';

type SelectCompound = React.ForwardRefExoticComponent<
    SelectProps & React.RefAttributes<SelectRef>
> &
    typeof Primitives;

export const Select = Object.assign(SelectFieldRoot, Primitives) as SelectCompound;
